import React from 'react';
import styled from 'styled-components';
import { capitalize } from 'affiny-core/helpers/string/capitalize';

import rem from '../../utils/rem';

interface IChip {
  className?: string;
  clickable?: boolean;
  readOnly?: boolean;
  color?: string;
  'data-test'?: string;
  isActive?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  text?: string;
  variant?: 'outlined' | 'contained';
}

const Chip = ({
  className,
  clickable = false,
  readOnly = false,
  color,
  'data-test': dataTest,
  isActive,
  onClick,
  text,
  variant,
}: IChip) => {
  return (
    <SChip
      className={className}
      clickable={clickable}
      color={color}
      data-test={dataTest}
      isActive={isActive}
      onClick={onClick}
      variant={variant}
      readOnly={readOnly}
    >
      {text !== undefined && text !== '' && <span>{capitalize(text)}</span>}
    </SChip>
  );
};

const SChip = styled.div<IChip>`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  transition: background-color 200ms ease 0s, color 200ms ease 0s;
  font-size: ${({ theme, readOnly }) =>
    readOnly ? theme.fontSizes.size14 : theme.fontSizes.size16};
  font-weight: ${({ variant }) => (variant === 'outlined' ? 500 : 600)};
  border-radius: ${({ text }) =>
    text !== undefined || text !== '' ? rem(50) : '50%'};
  border-width: ${({ variant }) => (variant === 'outlined' ? '2px' : 0)};
  border-style: ${({ variant }) => (variant === 'outlined' ? 'solid' : null)};
  border-color: ${({ theme, color, variant }) => {
    let localColor: string | undefined;

    if (variant === 'outlined') {
      switch (color) {
        case 'primary':
          localColor = theme.colors.primary2
            ? theme.colors.primary2
            : theme.colors.raspberry;
          break;
        case 'secondary':
          localColor = theme.colors.secondary2
            ? theme.colors.secondary2
            : theme.colors.paradiso;
          break;
        default:
          localColor = color !== undefined ? color : theme.colors.gallery;
          break;
      }
    }

    return localColor;
  }};
  color: ${({ color, theme, isActive, variant }) => {
    let local_color: string | undefined;

    if (variant === 'outlined') {
      return theme.colors.primary;
    } else {
      if (!isActive) {
        return theme.colors.secondary;
      } else {
        switch (color) {
          case 'primary':
            local_color = theme.colors.primary2;
            break;
          case 'subscription':
            local_color = theme.colors.subscription;
            break;
          case 'secondary':
            local_color = theme.colors.secondary2;
            break;
          default:
            local_color = color !== undefined ? color : 'inherit';
            break;
        }
      }
    }

    return local_color;
  }};
  background-color: ${({ isActive, theme, color, variant }) => {
    let bgColor = theme.colors.light;

    if (variant !== 'outlined') {
      if (!isActive) {
        return theme.colors.alabaster;
      } else {
        switch (color) {
          case 'primary':
            bgColor = theme.colors.pippin;
            break;
          case 'subscription':
            bgColor = theme.colors.subscription;
            break;
          case 'secondary':
            bgColor = theme.colors.secondary2;
            break;
          default:
            bgColor = color !== undefined ? color : 'rgb(0,0,0)';
            break;
        }
      }
    }

    return bgColor;
  }};
  height: ${({ theme }) =>
    theme.space ? theme.space.xLarge : theme.space.xLarge};
  padding: ${({ theme }) =>
    `${rem(4)} ${theme.space ? theme.space.medium : theme.space.medium} ${rem(
      6
    )} ${theme.space ? theme.space.medium : theme.space.medium}`};
  @media screen and (min-width: ${({ theme }) => theme.breakpoints[0]}) {
    &:hover {
      background-color: ${({ isActive, theme, color }) => {
        if (isActive) {
          switch (color) {
            case 'primary':
              return theme.colors.primaryHover;
            case 'subscription':
              return theme.colors.subscriptionHover;
            case 'secondary':
              return theme.colors.secondaryHover;
            default:
              return 'rgba(0,0,0,.05)';
          }
        }
      }};
      cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
    }
  }
`;

export default Chip;
